import type { NurseWithAllRelations, WorkShift } from "@dutyon/apis";
import type { MakeScheduleRequest } from "@dutyon/apis/algorithm";
import type {
  GpuGenerateScheduleOptions,
  GpuGenerationResult,
  GpuShadowDiagnostic,
  GpuWorkerProgress,
} from "@dutyon/apis/algorithm-gpu";
import type { DutyData } from "@dutyon/ui-kit";
import dayjs from "dayjs";
import type {
  AutoGenerateParams,
  AutoGenerateResult,
  ChunkResult,
} from "./autoGenerateSchedule.ts";
import { mergeIntoDutyData, toFixedSchedule } from "./dutyScheduleData.ts";
import { GPU_DUTY_MAX_SAMPLES, GPU_DUTY_MIN_SAMPLES } from "./gpuDutyGenerationConfig.ts";

export interface AutoGenerateGpuParams extends AutoGenerateParams {
  sampleCount: number;
  runSeed: number;
  /**
   * true면 현재 에디터에 입력된 모든 셀을 GPU 생성 중 변경할 수 없는
   * 배정으로 취급한다. 결과 병합 시 원래 isFixed 상태는 유지한다.
   */
  preserveExistingAssignments?: boolean;
  verificationMode?: GpuGenerateScheduleOptions["verificationMode"];
  canaryModulo?: number;
}

export interface GpuChunkProgress {
  chunkIndex: number;
  totalChunks: number;
  startDate: string;
  endDate: string;
  overallCompletedSamples: number;
  overallTotalSamples: number;
  worker: GpuWorkerProgress;
}

export interface AutoGenerateGpuResult extends AutoGenerateResult {
  completedSamples: number;
  requestedSamples: number;
  verifiedCandidateCount: number;
  completedChunks: number;
}

export type GenerateGpuScheduleFn = (
  runId: string,
  request: MakeScheduleRequest,
  options: GpuGenerateScheduleOptions,
  onProgress?: (progress: GpuWorkerProgress) => void,
  onDiagnostic?: (diagnostic: GpuShadowDiagnostic) => void,
) => Promise<GpuGenerationResult>;

export interface AutoGenerateGpuCallbacks {
  onProgress?: (progress: GpuChunkProgress) => void;
  onChunkComplete?: (result: ChunkResult, accumulatedData: DutyData) => void;
  onDiagnostic?: (diagnostic: GpuShadowDiagnostic) => void;
  onRunStart?: (runId: string) => void;
  onRunSettled?: (runId: string) => void;
  createRunId?: (chunkIndex: number) => string;
}

function defaultRunId(chunkIndex: number): string {
  const randomId = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `gpu-duty-${chunkIndex}-${randomId}`;
}

export function deriveGpuChunkSeed(runSeed: number, chunkIndex: number): number {
  return (runSeed ^ Math.imul(chunkIndex + 1, 0x9e37_79b9)) >>> 0;
}

function validateGpuOptions(params: AutoGenerateGpuParams): void {
  if (
    !Number.isSafeInteger(params.sampleCount) ||
    params.sampleCount < GPU_DUTY_MIN_SAMPLES ||
    params.sampleCount > GPU_DUTY_MAX_SAMPLES
  ) {
    throw new RangeError(
      `GPU sample 수는 ${GPU_DUTY_MIN_SAMPLES}~${GPU_DUTY_MAX_SAMPLES.toLocaleString(
        "ko-KR",
      )} 범위의 정수여야 합니다.`,
    );
  }
  if (!Number.isInteger(params.runSeed) || params.runSeed < 0 || params.runSeed > 0xffff_ffff) {
    throw new RangeError("GPU runSeed는 u32 범위의 정수여야 합니다.");
  }
}

function createRequest(options: {
  params: AutoGenerateGpuParams;
  nurses: NurseWithAllRelations[];
  workShifts: WorkShift[];
  accumulated: DutyData;
  chunkStart: dayjs.Dayjs;
  duration: number;
}): MakeScheduleRequest {
  const { params } = options;
  return {
    startDayString: options.chunkStart.format("YYYY-MM-DD"),
    nurses: options.nurses,
    workShifts: options.workShifts,
    rules: {
      maxConsecutiveDays: params.maxConsecutiveWorkDays ?? 6,
      nightConsecutiveMax: 3,
      useForbiddenPatterns: params.useForbiddenPatterns,
      useAvoidancePatterns: params.useAvoidancePatterns,
      usePongDangPatterns: params.usePongDangPatterns,
    },
    duration: options.duration,
    requirements: params.requirements,
    fixedSchedule: toFixedSchedule(options.accumulated, !params.preserveExistingAssignments),
  };
}

/**
 * 기존 CPU 오케스트레이터를 변경하지 않는 GPU 전용 생성 흐름이다.
 * 각 날짜 chunk에서 GPU 후보 sample을 탐색하고, CPU 검증을 통과한 결과만
 * 누적한다. 취소된 batch의 검증된 부분 결과는 반환하되 UI 적용 여부는
 * 호출자가 결정한다.
 */
export async function autoGenerateGpuSchedule(
  params: AutoGenerateGpuParams,
  generateSchedule: GenerateGpuScheduleFn,
  callbacks: AutoGenerateGpuCallbacks = {},
  signal?: AbortSignal,
): Promise<AutoGenerateGpuResult> {
  validateGpuOptions(params);

  const chunkSize = params.chunkSize ?? 31;
  if (!Number.isSafeInteger(chunkSize) || chunkSize < 1) {
    throw new RangeError("chunkSize는 1 이상의 안전한 정수여야 합니다.");
  }

  const totalDays = dayjs(params.endDate).diff(dayjs(params.startDate), "day") + 1;
  const totalChunks = Math.ceil(totalDays / chunkSize);
  const requestedSamples = params.sampleCount * totalChunks;
  const createRunId = callbacks.createRunId ?? defaultRunId;
  let accumulated: DutyData = { ...params.currentData };
  let totalScore = 0;
  let completedSamples = 0;
  let verifiedCandidateCount = 0;
  let completedChunks = 0;

  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
    if (signal?.aborted) {
      return {
        data: accumulated,
        totalScore,
        totalDays,
        success: true,
        cancelled: true,
        completedSamples,
        requestedSamples,
        verifiedCandidateCount,
        completedChunks,
      };
    }

    const chunkStart = dayjs(params.startDate).add(chunkIndex * chunkSize, "day");
    const remainingDays = totalDays - chunkIndex * chunkSize;
    const duration = Math.min(chunkSize, remainingDays);
    const chunkEnd = chunkStart.add(duration - 1, "day");
    const runId = createRunId(chunkIndex);
    const request = createRequest({
      params,
      nurses: params.nurses,
      workShifts: params.workShifts,
      accumulated,
      chunkStart,
      duration,
    });

    callbacks.onRunStart?.(runId);
    let result: GpuGenerationResult;
    try {
      result = await generateSchedule(
        runId,
        request,
        {
          runSeed: deriveGpuChunkSeed(params.runSeed, chunkIndex),
          sampleCount: params.sampleCount,
          verificationMode: params.verificationMode ?? "canary",
          canaryModulo: params.canaryModulo,
        },
        (worker) => {
          if (worker.runId !== runId) return;
          callbacks.onProgress?.({
            chunkIndex,
            totalChunks,
            startDate: chunkStart.format("MM/DD"),
            endDate: chunkEnd.format("MM/DD"),
            overallCompletedSamples: chunkIndex * params.sampleCount + worker.completedSamples,
            overallTotalSamples: requestedSamples,
            worker,
          });
        },
        callbacks.onDiagnostic,
      );
    } catch (error) {
      if (signal?.aborted) {
        return {
          data: accumulated,
          totalScore,
          totalDays,
          success: true,
          cancelled: true,
          completedSamples,
          requestedSamples,
          verifiedCandidateCount,
          completedChunks,
        };
      }
      return {
        data: accumulated,
        totalScore,
        totalDays,
        success: false,
        errorMessage: error instanceof Error ? error.message : "GPU Duty 생성에 실패했습니다.",
        completedSamples,
        requestedSamples,
        verifiedCandidateCount,
        completedChunks,
      };
    } finally {
      callbacks.onRunSettled?.(runId);
    }

    completedSamples += result.completedSamples;
    verifiedCandidateCount += result.verifiedCandidateCount;

    if (result.schedule) {
      accumulated = mergeIntoDutyData(accumulated, result.schedule);
      totalScore += result.best?.score ?? 0;
    }

    if (result.status === "cancelled") {
      return {
        data: accumulated,
        totalScore,
        totalDays,
        success: true,
        cancelled: true,
        completedSamples,
        requestedSamples,
        verifiedCandidateCount,
        completedChunks,
      };
    }

    if (!result.schedule || !result.finalCandidateVerified) {
      return {
        data: accumulated,
        totalScore,
        totalDays,
        success: false,
        errorMessage: `청크 ${chunkIndex + 1}/${totalChunks}의 GPU 결과를 CPU로 검증하지 못했습니다.`,
        completedSamples,
        requestedSamples,
        verifiedCandidateCount,
        completedChunks,
      };
    }

    completedChunks++;
    callbacks.onChunkComplete?.(
      {
        chunkIndex,
        totalChunks,
        startDate: chunkStart.format("MM/DD"),
        endDate: chunkEnd.format("MM/DD"),
        score: result.best?.score ?? 0,
        attempts: result.completedSamples,
        success: true,
      },
      { ...accumulated },
    );

    if (signal?.aborted) {
      return {
        data: accumulated,
        totalScore,
        totalDays,
        success: true,
        cancelled: true,
        completedSamples,
        requestedSamples,
        verifiedCandidateCount,
        completedChunks,
      };
    }
  }

  return {
    data: accumulated,
    totalScore,
    totalDays,
    success: true,
    completedSamples,
    requestedSamples,
    verifiedCandidateCount,
    completedChunks,
  };
}
